import React, { useState, useEffect } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Axios from 'axios';
import { message, Spin } from 'antd';
import {
  EyeOutlined,
  EyeInvisibleOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { redirectToLogin } from '../../../utils/sessionGuard';
import './TrialAccountPage.css';

// ステッパー（情報入力 → 完了）
const STEPS = [
  { n: 1, label: '情報入力' },
  { n: 2, label: '完了' },
];

// パスワード要件（ライブチェック）
const PW_RULES = [
  { key: 'len', label: '8文字以上', test: (v) => v.length >= 8 },
  { key: 'alpha', label: '英字を含む', test: (v) => /[A-Za-z]/.test(v) },
  { key: 'num', label: '数字を含む', test: (v) => /[0-9]/.test(v) },
];

/**
 * トライアル情報の登録画面。
 * 簡易登録（URLからの自動ログイン）では保存されない氏名・会社・部署と、
 * ご自身で決めたパスワードを登録する。正式ログイン後にのみ利用できる。
 */
function TrialProfilePage(props) {
  // 1: 情報入力フォーム, 2: 完了
  const [step, setStep] = useState(1);
  // 送信直後の完了画面表示中は、登録済み判定によるリダイレクトを行わないためのフラグ
  const [submitted, setSubmitted] = useState(false);
  // パスワード表示切替
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const userData = props.user ? props.user.userData : null;
  // トライアルユーザーか（キャンペーンキーの有無で判定）
  const isTrialUser = !!(userData && userData.trialCampaignKey);
  // 簡易ログイン中か（この状態では登録できない。サーバーも403で拒否する）
  const isQuickLogin = !!(userData && userData.trialQuickLogin === true);
  // 既に情報登録が完了しているか
  const isProfileCompleted = !!(userData && userData.trialProfileCompleted);

  // 画面の進入ガード（リダイレクトは副作用として実行する）
  useEffect(() => {
    if (!userData) return;
    // トライアルユーザーでなければトップへ
    if (!isTrialUser) {
      props.history.replace('/');
      return;
    }
    // 登録済みならトップへ。ただし送信直後の完了画面表示中は除く
    if (isProfileCompleted && !submitted) {
      props.history.replace('/');
    }
  }, [userData, isTrialUser, isProfileCompleted, submitted, props.history]);

  // ステッパー
  const renderStepper = (current) => (
    <ol className="stepper" aria-label="登録の進行状況">
      {STEPS.map((s) => {
        const cls =
          'step' +
          (s.n === current ? ' is-current' : '') +
          (s.n < current ? ' is-done' : '');
        return (
          <li key={s.n} className={cls}>
            <span className="step-num">{s.n}</span>
            <span className="step-label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );

  // ── ユーザー情報の読み込み中（認証HOCの処理中） ──
  if (!userData) {
    return (
      <div className="ds-trial-root">
        <div className="ds-trial-card">
          <div className="ds-trial-msg">
            <Spin />
          </div>
        </div>
      </div>
    );
  }

  // ── トライアルユーザーではない（useEffectでトップへ遷移） ──
  if (!isTrialUser) return null;

  // ── 簡易ログイン中は登録できない ──
  if (isQuickLogin) {
    return (
      <div className="ds-trial-root">
        <div className="ds-trial-card">
          {renderStepper(1)}
          <h1 className="form-title">トライアル情報の登録</h1>
          <div className="ds-trial-msg">
            メールでお送りしたログイン情報でログインしてから、こちらの登録を行ってください。
          </div>
        </div>
      </div>
    );
  }

  // ── 既に登録済み（useEffectでトップへ遷移） ──
  if (isProfileCompleted && !submitted) return null;

  return (
    <div className="ds-trial-root">
      <div className="ds-trial-card">
        {renderStepper(step)}

        {/* タイトル（完了画面では非表示） */}
        {step !== 2 && <h1 className="form-title">トライアル情報の登録</h1>}

        {/* ご案内（入力フォーム時のみ） */}
        {step === 1 && (
          <div className="apply-howto">
            <p>
              トライアルのご利用ありがとうございます。
              <br />
              結果の確認や継続してご利用いただくために、下記の情報をご登録ください。
            </p>
          </div>
        )}

        {/* ① 情報入力フォーム */}
        {step === 1 && (
          <Formik
            initialValues={{
              lastName: userData.lastname || '',
              name: userData.name || '',
              company: userData.company || '',
              department: userData.department || '',
              password: '',
              confirmPassword: '',
            }}
            validationSchema={Yup.object().shape({
              lastName: Yup.string().required('姓を入力してください。'),
              name: Yup.string().required('名を入力してください。'),
              company: Yup.string(),
              department: Yup.string(),
              password: Yup.string()
                .min(8, 'パスワードは8文字以上です。')
                .matches(/[A-Za-z]/, '英字を含めてください。')
                .matches(/[0-9]/, '数字を含めてください。')
                .required('パスワードを入力してください。'),
              confirmPassword: Yup.string()
                .oneOf(
                  [Yup.ref('password'), null],
                  'パスワードが一致しません。'
                )
                .required('パスワードを再入力してください。'),
            })}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                // サーバーの項目名に合わせる（Formikの lastName → lastname）
                const response = await Axios.patch(
                  '/api/trial/complete-profile',
                  {
                    lastname: values.lastName,
                    name: values.name,
                    company: values.company,
                    department: values.department,
                    password: values.password,
                    confirmPassword: values.confirmPassword,
                  },
                  {
                    headers: { 'x-user-token': localStorage.getItem('w_auth') },
                  }
                );

                if (response.data && response.data.success) {
                  // 完了画面へ（以降フォームはアンマウントされる）
                  setSubmitted(true);
                  setStep(2);
                  return;
                }

                message.error(
                  (response.data && response.data.message) ||
                    '登録中にエラーが発生しました。'
                );
              } catch (error) {
                const res = error.response;
                const data = res ? res.data : null;

                if (res && res.status === 400 && data && data.errors) {
                  // 入力値エラー（サーバー側バリデーション）
                  message.error(data.errors.map((e) => e.msg).join(', '));
                } else if (res && res.status === 401) {
                  // セッション切れ → ログイン画面へ
                  redirectToLogin({ withAlert: true });
                  return;
                } else if (data && data.message) {
                  message.error(data.message);
                } else {
                  message.error('登録中にエラーが発生しました。');
                }
              }
              setSubmitting(false);
            }}
          >
            {(formikProps) => {
              const {
                values,
                touched,
                errors,
                isSubmitting,
                handleChange,
                handleBlur,
                handleSubmit,
              } = formikProps;

              const invalid = (name) => touched[name] && errors[name];

              return (
                <form onSubmit={handleSubmit} noValidate>
                  <p className="form-legend">
                    <span className="req" aria-hidden="true">
                      *
                    </span>
                    は必須項目です
                  </p>

                  {/* メール（変更不可） */}
                  <div className="field">
                    <label className="field-label" htmlFor="trial-email">
                      メール
                      <span className="req" aria-label="必須">
                        *
                      </span>
                    </label>
                    <input
                      className="input"
                      id="trial-email"
                      type="email"
                      value={userData.email || ''}
                      readOnly
                      aria-readonly="true"
                    />
                    <p className="field-hint">
                      ログイン中のアカウントです（変更できません）。
                    </p>
                  </div>

                  {/* 名前（姓） */}
                  <div className="field">
                    <label className="field-label" htmlFor="lastName">
                      名前（姓）
                      <span className="req" aria-label="必須">
                        *
                      </span>
                    </label>
                    <input
                      className={
                        'input' + (invalid('lastName') ? ' is-invalid' : '')
                      }
                      id="lastName"
                      type="text"
                      placeholder="例）新宿"
                      autoComplete="family-name"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {invalid('lastName') && (
                      <p className="field-error">
                        <ExclamationCircleOutlined />
                        {errors.lastName}
                      </p>
                    )}
                  </div>

                  {/* 名前（名） */}
                  <div className="field">
                    <label className="field-label" htmlFor="name">
                      名前（名）
                      <span className="req" aria-label="必須">
                        *
                      </span>
                    </label>
                    <input
                      className={
                        'input' + (invalid('name') ? ' is-invalid' : '')
                      }
                      id="name"
                      type="text"
                      placeholder="例）太郎"
                      autoComplete="given-name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    {invalid('name') && (
                      <p className="field-error">
                        <ExclamationCircleOutlined />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  {/* 会社名（任意） */}
                  <div className="field">
                    <label className="field-label" htmlFor="company">
                      会社名<span className="optional">任意</span>
                    </label>
                    <input
                      className="input"
                      id="company"
                      type="text"
                      placeholder="例）リ・カレント株式会社"
                      autoComplete="organization"
                      value={values.company}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>

                  {/* 部署名（任意） */}
                  <div className="field">
                    <label className="field-label" htmlFor="department">
                      部署名<span className="optional">任意</span>
                    </label>
                    <input
                      className="input"
                      id="department"
                      type="text"
                      placeholder="例）人材開発部"
                      autoComplete="organization-title"
                      value={values.department}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>

                  {/* 新しいパスワード */}
                  <div className="field">
                    <label className="field-label" htmlFor="password">
                      新しいパスワード
                      <span className="req" aria-label="必須">
                        *
                      </span>
                    </label>
                    <div className="input-pw">
                      <input
                        className={
                          'input' + (invalid('password') ? ' is-invalid' : '')
                        }
                        id="password"
                        type={showPw ? 'text' : 'password'}
                        placeholder="パスワードを入力"
                        autoComplete="new-password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <button
                        type="button"
                        className="pw-toggle"
                        aria-label={
                          showPw ? 'パスワードを隠す' : 'パスワードを表示'
                        }
                        onClick={() => setShowPw((s) => !s)}
                      >
                        {showPw ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      </button>
                    </div>
                    <p className="field-hint">
                      メールでお送りしたパスワードから、ご自身が覚えやすいものに変更してください。
                    </p>
                    {/* パスワード要件 ライブチェック */}
                    <ul className="pw-check" aria-live="polite">
                      {PW_RULES.map((rule) => {
                        const ok = rule.test(values.password || '');
                        return (
                          <li key={rule.key} className={ok ? 'is-ok' : ''}>
                            <span className="dot">
                              <CheckOutlined />
                            </span>
                            {rule.label}
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {/* 新しいパスワード（確認） */}
                  <div className="field">
                    <label className="field-label" htmlFor="confirmPassword">
                      新しいパスワード（確認）
                      <span className="req" aria-label="必須">
                        *
                      </span>
                    </label>
                    <div className="input-pw">
                      <input
                        className={
                          'input' +
                          (invalid('confirmPassword') ? ' is-invalid' : '')
                        }
                        id="confirmPassword"
                        type={showPw2 ? 'text' : 'password'}
                        placeholder="確認のためもう一度入力"
                        autoComplete="new-password"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                      <button
                        type="button"
                        className="pw-toggle"
                        aria-label={
                          showPw2 ? 'パスワードを隠す' : 'パスワードを表示'
                        }
                        onClick={() => setShowPw2((s) => !s)}
                      >
                        {showPw2 ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      </button>
                    </div>
                    {/* 一致表示 */}
                    {values.confirmPassword && (
                      <p
                        className={
                          'match-msg ' +
                          (values.confirmPassword === values.password
                            ? 'is-ok'
                            : 'is-ng')
                        }
                      >
                        {values.confirmPassword === values.password ? (
                          <>
                            <CheckOutlined />
                            パスワードが一致しました
                          </>
                        ) : (
                          <>
                            <CloseOutlined />
                            パスワードが一致しません
                          </>
                        )}
                      </p>
                    )}
                  </div>

                  <div className="form-submit">
                    <button
                      className="btn"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      登録する
                    </button>
                  </div>
                </form>
              );
            }}
          </Formik>
        )}

        {/* ② 完了 */}
        {step === 2 && (
          <div className="complete">
            <div className="complete-icon" aria-hidden="true">
              <CheckCircleOutlined />
            </div>
            <h2 className="complete-title">登録が完了しました</h2>
            <p className="complete-text">
              引き続きサービスをご利用いただけます。
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => window.location.replace('/')}
            >
              トップページへ
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrialProfilePage;
